import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Info,
  Calendar,
  ArrowRight,
  ClipboardCheck,
  Clock,
  Shield,
  Hammer,
  Truck,
  CheckSquare,
} from "lucide-react";

export default function RoofReplacementProcessStepByStep() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/roof-replacement-process-step-by-step#article",
          headline:
            "Roof Replacement Process: What to Expect from Inspection to Final Walkthrough",
          description:
            "Nervous about getting a new roof? We walk you through our complete step-by-step roof replacement process in Saskatchewan, so you know exactly what to expect.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-05-15",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/roof-replacement-process-step-by-step",
          url: "https://www.saskroofingcompany.com/blog/roof-replacement-process-step-by-step",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Roof Replacement Process",
              item: "https://www.saskroofingcompany.com/blog/roof-replacement-process-step-by-step",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How long does a roof replacement take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "For most standard residential homes in Saskatchewan, a complete roof replacement takes 1 to 2 days. Larger, steeper, or highly complex roofs may take 3 to 4 days, weather permitting.",
          },
        },
        {
          "@type": "Question",
          name: "Do I need to be home during the replacement?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "You do not need to be home while the crew is actively working on the roof. However, we do recommend being present for the initial morning walkthrough and the final post-job walkthrough.",
          },
        },
        {
          "@type": "Question",
          name: "What should I do to prepare my home for roof replacement?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Please move all vehicles from the driveway to allow space for the disposal bin and material delivery. You should also clear the perimeter of your house, move patio furniture, and secure any fragile items in the attic, as the vibrations can cause dust to fall.",
          },
        },
        {
          "@type": "Question",
          name: "How messy is roof replacement? Will my landscaping be damaged?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Roof tear-off is inherently messy, but a professional crew manages it tightly. We use tarps to protect your landscaping and perform a thorough magnetic sweep of your entire yard and driveway afterward to pick up stray nails.",
          },
        },
        {
          "@type": "Question",
          name: "What if weather delays the project?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We monitor Saskatchewan's weather closely. If a storm is forecasted, we will reschedule your start date. If unexpected weather rolls in while we are working, we immediately secure and tarp the roof to ensure your home remains completely watertight.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roof Replacement Process Step-by-Step | Sask Roofing"
        description="Our complete step-by-step roof replacement process in Saskatchewan, so you know exactly what to expect from inspection to final walkthrough."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/1600982c-b626-48b2-8bd2-2a601866b6a7.png&output=webp&q=60"
            alt="Aerial view of a residential roof in-progress tear-off in Saskatchewan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published May 15, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roof Replacement Process: What to Expect Step-by-Step
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Getting a new roof is a major home improvement project. We walk
              you through a typical project from the first phone call to the
              final nail, so you know exactly what to expect.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                For most homeowners, a{" "}
                <Link
                  to="/services/replacement"
                  className="text-primary font-medium hover:underline"
                >
                  roof replacement
                </Link>{" "}
                is something you only go through once or twice in your lifetime.
                It's a significant investment, and the thought of having a
                construction crew tearing apart the top of your house can be
                stressful.
              </p>
              <p>
                At Sask Roofing Company, we believe that anxiety comes from the
                unknown. When you know exactly what is going to happen, when
                it's going to happen, and who is responsible for it, the process
                is actually quite smooth. Here is our complete, transparent
                guide to exactly how we replace a roof in Saskatchewan.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Clock className="w-6 h-6 text-primary" />
                The Process at a Glance
              </h2>
              <p className="text-lg text-muted-foreground mb-0">
                Most residential roof replacements take just{" "}
                <strong>1 to 2 days</strong> to complete. The process begins
                with a free inspection, followed by a written quote, material
                delivery, a complete tear-off of the old roof, installation of
                the new system, and a final magnetic sweep and walkthrough.
              </p>
            </div>

            {/* The Timeline Steps */}
            <div className="space-y-10 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent">
              {/* Step 1 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  1
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-primary" /> Free
                    Inspection
                  </h3>
                  <p className="text-muted-foreground">
                    Everything starts with a thorough assessment. We don't just
                    look at the shingles from the ground. We climb up and
                    examine the flashing, eavestroughs, and chimney. We also
                    inspect your attic ventilation and look for signs of hidden
                    moisture or decking rot. You get a same-day, honest
                    diagnosis: whether it's cosmetic wear, storm damage, or
                    structural failure.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  2
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-primary" /> Custom
                    Quote & Selection
                  </h3>
                  <p className="text-muted-foreground">
                    We walk you through your material choices. Whether you want
                    high-wind architectural asphalt or a premium standing seam
                    metal roof, we explain the pros, cons, and{" "}
                    <Link
                      to="/roofing-cost-guide-saskatchewan"
                      className="text-primary hover:underline"
                    >
                      costs
                    </Link>
                    . We provide a fixed written quote. The price on the paper
                    is the price you pay—no hidden fees. Take your time to
                    decide; there is no high-pressure sales pitch.
                  </p>
                </div>
              </div>

              {/* Inline Photo 1 */}
              <div className="relative z-10 my-10 bg-background p-2">
                <figure className="rounded-xl overflow-hidden shadow-md">
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f6e6237e-6e8d-4ee9-9904-fc2bc8303b61.png&output=webp&q=60"
                    alt="Contractor handing a clipboard with a written quote to a homeowner"
                    className="w-full h-auto object-cover aspect-[21/9]"
                  />
                  <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                    You will receive a detailed, fixed-price written quote
                    outlining exactly what is included in your roof replacement.
                  </figcaption>
                </figure>
              </div>

              {/* Step 3 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  3
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" /> Scheduling &
                    Pre-Job Prep
                  </h3>
                  <p className="text-muted-foreground">
                    Once you approve the quote, we schedule your project
                    (typically 2-4 weeks out, depending on the season). A week
                    before we start, we'll confirm the date. We'll ask you to
                    move your vehicles out of the driveway so we can safely
                    deliver the materials and place our disposal bin.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  4
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Hammer className="w-5 h-5 text-primary" /> Tear-Off &
                    Installation (Day 1)
                  </h3>
                  <p className="text-muted-foreground">
                    Our crew arrives bright and early. We protect your
                    landscaping with tarps and begin the tear-off. We do not
                    shingle over old roofs. We strip it down to the bare wood
                    decking, inspect for rot, and replace any damaged sheets.
                    Then, we install ice and water shield, synthetic
                    underlayment, and begin nailing down your new shingles. By
                    the end of Day 1, a typical home is 60-70% complete and
                    fully watertight.
                  </p>
                </div>
              </div>

              {/* Inline Photo 2 */}
              <div className="relative z-10 my-10 bg-background p-2">
                <figure className="rounded-xl overflow-hidden shadow-md">
                  <img
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/47752e20-2a3f-4c23-9329-033c5d9ac033.png&output=webp&q=60"
                    alt="Dumpster and new roofing shingles delivery in front of a residential home"
                    className="w-full h-auto object-cover aspect-[21/9]"
                  />
                  <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                    Materials and a disposal bin are delivered directly to your
                    driveway to ensure a clean, efficient tear-off process.
                  </figcaption>
                </figure>
              </div>

              {/* Step 5 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  5
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary" /> Final Touches &
                    Cleanup (Day 2)
                  </h3>
                  <p className="text-muted-foreground">
                    We finish the main field shingles and move on to the
                    critical details: ridge caps, roof vents, and custom metal
                    flashing around chimneys and walls. Once the roof is
                    perfect, the cleanup begins. We clear your gutters of debris
                    and run powerful magnetic sweepers over your lawn, garden
                    beds, and driveway to ensure no stray nails are left behind.
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  6
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-2xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" /> Final
                    Walkthrough & Warranty
                  </h3>
                  <p className="text-muted-foreground">
                    Before we pull the disposal bin away, our project manager
                    walks the property with you. We show you the completed work,
                    answer any questions, and ensure you are 100% satisfied. You
                    receive your written workmanship warranty and manufacturer
                    warranty documents. The job isn't done until you sign off.
                  </p>
                </div>
              </div>
            </div>

            {/* What You Need to Do List */}
            <div className="bg-accent/10 border border-accent/20 p-8 rounded-xl my-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                What You'll Need to Do
              </h2>
              <p className="text-muted-foreground mb-6">
                While we handle the heavy lifting, there are a few simple things
                we ask homeowners to do to ensure the project runs smoothly:
              </p>
              <ul className="space-y-4">
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <strong className="block text-foreground">
                      Clear the driveway.
                    </strong>
                    <span className="text-muted-foreground">
                      Move your vehicles to the street or a neighbor's house the
                      night before so we can drop the bin and materials first
                      thing in the morning.
                    </span>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <strong className="block text-foreground">
                      Secure fragile items.
                    </strong>
                    <span className="text-muted-foreground">
                      Tearing off a roof creates vibrations. Take down loosely
                      hung pictures on the top floor and cover items stored in
                      the attic to protect them from dust.
                    </span>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <strong className="block text-foreground">
                      Keep pets inside.
                    </strong>
                    <span className="text-muted-foreground">
                      A roofing site is loud and there will be debris falling
                      during the tear-off phase. Keep pets safely indoors until
                      the final magnetic sweep is complete.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
              <Accordion
                type="single"
                collapsible
                className="w-full bg-card border rounded-xl px-6 py-2 shadow-sm"
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    How long does a roof replacement take?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    For most standard residential homes in Saskatchewan, a
                    complete roof replacement takes 1 to 2 days. Larger,
                    steeper, or highly complex roofs may take 3 to 4 days,
                    weather permitting.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Do I need to be home during the replacement?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    You do not need to be home while the crew is actively
                    working on the roof. However, we do recommend being present
                    for the initial morning walkthrough and the final post-job
                    walkthrough.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    What should I do to prepare my home for roof replacement?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Please move all vehicles from the driveway to allow space
                    for the disposal bin and material delivery. You should also
                    clear the perimeter of your house, move patio furniture, and
                    secure any fragile items in the attic, as the vibrations can
                    cause dust to fall.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    How messy is roof replacement? Will my landscaping be
                    damaged?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Roof tear-off is inherently messy, but a professional crew
                    manages it tightly. We use tarps to protect your landscaping
                    and perform a thorough magnetic sweep of your entire yard
                    and driveway afterward to pick up stray nails.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    What if weather delays the project?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    We monitor Saskatchewan's weather closely. If a storm is
                    forecasted, we will reschedule your start date. If
                    unexpected weather rolls in while we are working, we
                    immediately secure and tarp the roof to ensure your home
                    remains completely watertight.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/d051f9d1-d071-48d5-a325-16bed6ea3e64.png&output=webp&q=60')] opacity-20 bg-cover bg-center mix-blend-overlay"></div>
        <div className="container px-4 relative z-10 text-center max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Start Step 1?
          </h2>
          <p className="text-xl text-primary-foreground/90">
            It all begins with a free, no-obligation inspection. We'll assess
            your roof and give you the honest facts about its condition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              Book a Free Inspection
            </Link>
            <Link
              to="/process"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-transparent border-2 border-primary-foreground text-primary-foreground rounded-md hover:bg-primary-foreground/10 transition-colors"
            >
              Read About Our Process
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
